import React, { useCallback, useState } from "react";
import { getIcon } from "@apps/mf-overview/src/assets/icons";
import { cx } from "@linaria/core";
import { easings } from "../../../config";

type Props = {
  goToPortals: () => Promise<void>;
  arePortalsReady: boolean;
};

export function DeprecationMessage(props: Props): React.JSX.Element {
  const [showThrobber, setShowThrobber] = useState(false);

  const handleClick = useCallback(() => {
    if (!props.arePortalsReady) {
      setShowThrobber(true);
    }

    props.goToPortals();
  }, [props.arePortalsReady, props.goToPortals]);

  return (
    <div className="wrapper">
      <h1>Forløbsbyggeren er blevet en del af portalerne!</h1>

      <p>
        Bare rolig, dine forløb findes stadig - vi har bare lagt byggeren dér
        hvor du skal bruge den.
        <br />
        Du kan nu direkte fra Gyldendals fagportaler tilgå, redigere og oprette
        forløb.
        <br />
        <br />
        På portalerne kan du tilgå dine forløb i dit brugerområde{" "}
        <span className="icon-wrapper">
          {getIcon("pm2-user", {
            strokeColor: "currentColor",
            fillColor: "none",
            strokeHoverColor: "currentColor",
            fillHoverColor: "none",
          })}
        </span>{" "}
        under <b>Mine forløb</b> eller på{" "}
        <span className="icon-wrapper">
          {getIcon("pm2-courses", {
            strokeColor: "currentColor",
            fillColor: "none",
            strokeHoverColor: "currentColor",
            fillHoverColor: "none",
          })}
        </span>{" "}
        ikonet i toppen af portalen.
      </p>

      <button
        onClick={handleClick}
        className={cx(showThrobber && "showThrobber")}
      >
        <span className={cx("content", !showThrobber && "visible")}>
          Vælg en fagportal og prøv det nu
        </span>
        <span className={cx("throbber", showThrobber && "visible")} />
      </button>

      <style jsx>
        {`
          .wrapper {
            display: flex;
            flex-direction: column;

            max-width: 670px;
            margin: 0 auto;
          }

          h1 {
            margin: 0;
            font-size: 30px;
            font-weight: 800;
            margin-bottom: 87px;
            line-height: 1.1;
          }

          p {
            font-size: 18px;
            line-height: 1.5;
            font-weight: 400;
          }

          .icon-wrapper {
            display: inline-block;
            vertical-align: middle;
            margin: 0 5px;

            & > :global(span) {
              display: block;
              width: auto;
              height: auto;

              & > :global(svg) {
                width: auto;
                height: auto;
              }
            }
          }

          button {
            position: relative;

            appearance: none;
            outline: none;
            border: none;
            margin: 80px auto 0;
            cursor: pointer;

            height: 58px;
            border-radius: 4px;
            padding: 0 25px;
            background-color: #ea7e00;

            font-size: 18px;
            font-weight: 700;
            color: #fff;

            transition: background-color 0.1s linear;

            &:hover {
              background-color: #bb6400;
            }

            &:focus-visible {
              box-shadow: 0 0 0 3px #fff, 0 0 0 6px grey;
            }
          }

          .content {
            opacity: 0;
            transition: opacity 0.15s linear;

            &.visible {
              opacity: 1;
            }
          }

          .throbber {
            position: absolute;
            top: 50%;
            left: 50%;

            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
            transition: opacity 0.15s linear,
              transform 0.15s ${easings.inOutQuad};

            &.visible {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
            }

            &::before {
              content: "";

              display: block;
              width: 1.8rem;
              height: 1.8rem;
              border-radius: 50%;
              border: 2px solid rgba(255, 255, 255, 0.25);
              border-left-color: rgba(255, 255, 255, 0.75);

              animation: spin 0.9s linear infinite;
            }

            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          }
        `}
      </style>
    </div>
  );
}
